import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cx from 'classnames';
import { convertToRaw } from 'draft-js';
import { isEmpty, isNil } from 'ramda';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import ItemLink from 'components/Layouts/Admin/Dashboard/Navigation/ItemLink';
import { ItineraryDetailsSearchFilterButton } from 'components/Pages/Admin/shared/SearchFilterForDetailsTab';
import AddMinusButton from 'components/shared/AddMinusButton';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import TableMenu from 'components/shared/TableMenu';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import {
  copyScheduleItemAction,
  createScheduleItemAction,
  deleteScheduleItemAction,
  moveScheduleItemAction,
  setAccommodationContentStateAction,
  setAccommodationRemovedPageAction,
  setDatesRangeAction,
  setDaysRangeAction,
  setDetailsContentStateAction,
  setDetailsRemovedPageAction,
} from 'store/itineraryDetails';
import { getItineraryScheduleTypeSelector, getScheduleItemsSelector } from 'store/itineraryDetails/selectors';
import { debounce } from 'utils/debounce';
import { determineRangeStart, extractExtraRanges, findNearestLeftRangeForType } from 'utils/itineraryDetails';
import { SCHEDULE_ITEM_TYPES, SCHEDULE_TYPES } from 'utils/itineraryDetails/constants';

import ActionsMenu from '../ActionsMenu';
import DatePickerButton from '../DatePickerButton';
import DayPickerButton from '../DayPickerButton';
import { useAddScheduleMenuActions } from '../DetailsOriginalMode/functions';
import MentionEditor from '../MentionEditor';
import { EDITOR_TYPES } from '../MentionEditor/constants';
import ChooseFirstDate from './ChooseFirstDate';
import { useDetailsTable } from './functions';
import styles from './index.module.css';
import SwitchOrganizedBy from './SwitchOrganizedBy';
import SwitchToChronoMode from './SwitchToChronoMode';

export default function DetailsTable({ onTableMount, shouldRegisterMentionRemovalRef, mountEl }) {
  const dispatch = useDispatch();
  const scheduleType = useSelector(getItineraryScheduleTypeSelector);
  const itinerary = useSelector(itinerariesItemDataSelector);
  const scheduleItems = useSelector(getScheduleItemsSelector);
  const { isTabletOrMobile } = useMediaBreakpoints();
  const scheduleItemDndIds = useMemo(() => scheduleItems.map((v) => String(v.id)), [scheduleItems]);
  const isNoData = isEmpty(scheduleItems);
  const [draggingItemId, setDraggingItemId] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const numberOfFilters =
    itinerary.filtersForDetailsTab?.data?.tags?.length + (itinerary.filtersForDetailsTab?.data?.show ? 1 : 0);

  const dateOrDays = scheduleType === SCHEDULE_TYPES.DATES ? 'Date' : 'Days';

  const draggingItem = useMemo(() => {
    const idx = scheduleItemDndIds.indexOf(draggingItemId);
    if (idx === -1) {
      return null;
    }
    return scheduleItems[idx];
  }, [draggingItemId, scheduleItemDndIds, scheduleItems]);

  const setDetailsContentState = useCallback(
    debounce(
      (id, contentState) =>
        dispatch(
          setDetailsContentStateAction({
            id,
            rawContentState: convertToRaw(contentState),
          }),
        ),
      200,
    ),
    [],
  );

  const setAccommodationContentState = useCallback(
    debounce(
      (id, contentState) =>
        dispatch(
          setAccommodationContentStateAction({
            id,
            rawContentState: convertToRaw(contentState),
          }),
        ),
      200,
    ),
    [],
  );

  function handleDragEnd(event) {
    document.body.classList.remove('dragging');
    setDraggingItemId(null);
    const { active, over } = event;
    if (!(active && over)) {
      return;
    }
    if (active.id !== over.id) {
      const oldIndex = scheduleItemDndIds.indexOf(active.id);
      const newIndex = scheduleItemDndIds.indexOf(over.id);
      dispatch(
        moveScheduleItemAction({
          sourceItem: scheduleItems[oldIndex],
          targetItem: scheduleItems[newIndex],
        }),
      );
    }
  }

  useEffect(() => {
    if (onTableMount) onTableMount();
  }, []);

  const { switchModeActions, organizedByActions } = useDetailsTable();

  return (
    <div>
      <div className={styles.buttonsContainer}>
        <ChooseFirstDate mountEl={mountEl} />
        <div className={styles.circledActions}>
          <SwitchOrganizedBy actions={organizedByActions} />
          <ItineraryDetailsSearchFilterButton
            isMobile={isTabletOrMobile}
            filter={itinerary.filtersForDetailsTab}
            filterName="Content Pages"
            filterKeyName="filtersForDetailsTab"
            className={styles.filterContainer}
            numberOfFilters={numberOfFilters}
            isRoundButton
          />
          <SwitchToChronoMode actions={switchModeActions} />
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={({ active }) => {
          if (!active) {
            return;
          }
          document.body.classList.add('dragging');
          setDraggingItemId(active.id);
        }}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          document.body.classList.remove('dragging');
          setDraggingItemId(null);
        }}
      >
        <SortableContext items={scheduleItemDndIds} strategy={verticalListSortingStrategy}>
          <div className={cx(styles.table, isNoData && styles.noDataTable)}>
            <div className={styles.row}>
              <div className={styles.headCell}></div>

              <div className={styles.headCell}>{dateOrDays}</div>
              <div className={styles.headCell}>Transfers, Flights, and Daily Activities</div>
              <div className={styles.headCell}>Accommodations & Rooming</div>

              <div className={styles.headCell}></div>
            </div>

            {!isNoData &&
              scheduleItems.map((scheduleItem) => {
                return (
                  <ReorderableItem key={scheduleItem.id} dndId={String(scheduleItem.id)}>
                    <RowContent
                      mountEl={mountEl}
                      setAccommodationContentState={setAccommodationContentState}
                      setDetailsContentState={setDetailsContentState}
                      scheduleType={scheduleType}
                      scheduleItems={scheduleItems}
                      scheduleItem={scheduleItem}
                      shouldRegisterMentionRemovalRef={shouldRegisterMentionRemovalRef}
                    />
                  </ReorderableItem>
                );
              })}

            {isNoData && (
              <div className={styles.row}>
                <div className={styles.cell} />
                <div className={styles.cell} />
                <div className={styles.cell} />
                <div className={styles.cell} />
                <div className={styles.cell} />
              </div>
            )}
            {isNoData && <div className={styles.noDataPlaceholder}>No data</div>}

            {createPortal(
              <DragOverlay adjustScale={false} className={styles.dragOverlay}>
                {draggingItemId ? (
                  <DragOverlayItem
                    key={draggingItem.id}
                    id={draggingItem.id}
                    scheduleType={scheduleType}
                    scheduleItems={scheduleItems}
                    scheduleItem={draggingItem}
                  ></DragOverlayItem>
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </div>
        </SortableContext>
      </DndContext>

      <FormActions isCenter marginTop>
        <AddMinusButton onClick={() => dispatch(createScheduleItemAction())} />
      </FormActions>
    </div>
  );
}

function RowContent({
  scheduleItem,
  setDetailsContentState,
  setAccommodationContentState,
  scheduleType,
  scheduleItems,
  withoutMenu,
  mountEl,
  shouldRegisterMentionRemovalRef,
}) {
  const dispatch = useDispatch();
  const doesRowHaveType = !isNil(scheduleItem.type);
  const datePickerRef = useRef();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const [active, setActive] = useState(false);

  const handleAfterTypeSelected = () => {
    if (datePickerRef.current?.open) {
      datePickerRef.current.open();
    }
  };

  const renderNoTypeClickableCell = () => {
    return <NoTypeClickableCell scheduleItemId={scheduleItem.id} onAfterTypeSelected={handleAfterTypeSelected} />;
  };

  return (
    <>
      <div className={cx(styles.cell, active && styles.cellActive)}>
        {scheduleType === SCHEDULE_TYPES.DATES && (
          <DatePickerButton
            setActive={setActive}
            elementRef={datePickerRef}
            mountEl={mountEl}
            readOnly={!doesRowHaveType}
            onChange={(range) => dispatch(setDatesRangeAction({ id: scheduleItem.id, range }))}
            range={scheduleItem.datesRange}
            getExtraRanges={() => extractExtraRanges(scheduleType, scheduleItems, scheduleItem.id)}
            showNightsCount={scheduleItem.type === SCHEDULE_ITEM_TYPES.OVERNIGHT}
            showRangeLabels={scheduleItem.type === SCHEDULE_ITEM_TYPES.OVERNIGHT}
            noRange={scheduleItem.type === SCHEDULE_ITEM_TYPES.DAY_ROOM}
            scheduleItem={scheduleItem}
            determineSelectedRangeStart={() => determineRangeStart(scheduleType, scheduleItems, scheduleItem.id)}
            determineCalendarStartDate={() => {
              if (scheduleItem.datesRange.end ?? scheduleItem.datesRange.start) {
                return scheduleItem.datesRange.end ?? scheduleItem.datesRange.start;
              }
              const range = findNearestLeftRangeForType(scheduleType, scheduleItems, scheduleItem);
              if (!range) return null;
              return range.end ?? range.start;
            }}
          />
        )}
        {scheduleType === SCHEDULE_TYPES.DAYS && (
          <DayPickerButton
            setActive={setActive}
            elementRef={datePickerRef}
            readOnly={!doesRowHaveType}
            mountEl={mountEl}
            onChange={(range) => dispatch(setDaysRangeAction({ id: scheduleItem.id, range }))}
            range={scheduleItem.daysRange}
            getExtraRanges={() => extractExtraRanges(scheduleType, scheduleItems, scheduleItem.id)}
            showNightsCount={scheduleItem.type === SCHEDULE_ITEM_TYPES.OVERNIGHT}
            showRangeLabels={scheduleItem.type === SCHEDULE_ITEM_TYPES.OVERNIGHT}
            noRange={scheduleItem.type === SCHEDULE_ITEM_TYPES.DAY_ROOM}
            scheduleItem={scheduleItem}
            determineSelectedRangeStart={
              scheduleItem.type === SCHEDULE_ITEM_TYPES.OVERNIGHT || scheduleItem.type === SCHEDULE_ITEM_TYPES.DAY_ROOM
                ? () => determineRangeStart(scheduleType, scheduleItems, scheduleItem.id)
                : undefined
            }
          />
        )}

        {!doesRowHaveType && renderNoTypeClickableCell()}
      </div>
      <div className={styles.cell}>
        <MentionEditor
          readOnly={!doesRowHaveType}
          additionalFilterQueryParams={itinerary.filtersForDetailsTab?.data}
          mountEl={mountEl}
          onContentStateChange={(contentState) => {
            setDetailsContentState(scheduleItem.id, contentState);
          }}
          initialRawContentState={scheduleItem.details?.contentState}
          placeholder="Fly via private charter flight to the Maasai Mara, approximately one hour flying time."
          type={EDITOR_TYPES.DETAILS}
          onRemoveMention={(meta) => {
            if (!shouldRegisterMentionRemovalRef?.current || isNil(meta.id)) {
              return;
            }

            dispatch(setDetailsRemovedPageAction({ id: scheduleItem.id, pageId: meta.id }));
          }}
        />

        {!doesRowHaveType && renderNoTypeClickableCell()}
      </div>
      <div
        className={styles.cell}
        style={{
          backgroundColor: scheduleItem.type === SCHEDULE_ITEM_TYPES.ACTIVITY ? '#f2f2f2' : undefined,
        }}
      >
        {scheduleItem.type !== SCHEDULE_ITEM_TYPES.ACTIVITY && (
          <MentionEditor
            readOnly={!doesRowHaveType}
            mountEl={mountEl}
            onContentStateChange={(contentState) => setAccommodationContentState(scheduleItem.id, contentState)}
            initialRawContentState={scheduleItem.accommodation?.contentState}
            pages={scheduleItem.accommodation?.pages}
            placeholder="Overnight @Emakoko"
            type={EDITOR_TYPES.ACCOMMODATION}
            additionalFilterQueryParams={itinerary.filtersForDetailsTab?.data}
            onRemoveMention={(meta) => {
              if (!shouldRegisterMentionRemovalRef?.current || isNil(meta.id)) {
                return;
              }

              dispatch(setAccommodationRemovedPageAction({ id: scheduleItem.id, pageId: meta.id }));
            }}
          />
        )}

        {!doesRowHaveType && renderNoTypeClickableCell()}
      </div>

      {!withoutMenu && (
        <TableMenu
          renderCell={({ toggle, className, icon }) => {
            return (
              <div className={cx(className, styles.cell)} onClick={toggle}>
                {icon}
              </div>
            );
          }}
          placementParams={{ anchor: 'BOTTOM_CENTER', triggerOffset: 15 }}
          isFixed
        >
          {({ close }) => {
            return (
              <>
                <ItemLink
                  isButton
                  isDropdown
                  onClick={() => {
                    dispatch(copyScheduleItemAction({ id: scheduleItem.id }));
                    close();
                  }}
                >
                  Duplicate
                </ItemLink>

                <ItemLink
                  style={{ color: 'var(--text_color-critical)' }}
                  isButton
                  isDropdown
                  onClick={() => {
                    dispatch(deleteScheduleItemAction({ id: scheduleItem.id }));
                    close();
                  }}
                >
                  Delete
                </ItemLink>
              </>
            );
          }}
        </TableMenu>
      )}
    </>
  );
}

function NoTypeClickableCell({ scheduleItemId, onAfterTypeSelected }) {
  const addScheduleMenuActions = useAddScheduleMenuActions({
    scheduleItemId,
    onAfterTypeSelected,
  });

  return (
    <div className={styles.activeMenuWrapper}>
      <ActionsMenu
        className={styles.activeMenu}
        actions={addScheduleMenuActions}
        childrenWrapperClassName={styles.childrenWrapper}
      />
    </div>
  );
}

function ReorderableItem({ dndId, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dndId });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    userSelect: 'none',
    pointerEvents: isDragging ? 'none' : undefined,
    opacity: isDragging ? 0.2 : undefined,
  };

  return (
    <div ref={setNodeRef} className={styles.row} style={style} {...attributes}>
      <div className={styles.dragHandleCell}>
        <div
          className={cx(styles.dragHandle, isDragging && styles.dragHandleDragging)}
          {...listeners}
          style={{
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          <DragHandleIcon />
        </div>
      </div>
      {children}
    </div>
  );
}

function DragOverlayItem({ scheduleItem, scheduleType, scheduleItems }) {
  return (
    <div className={cx(styles.table, styles.dragOverlayTable)}>
      <div className={styles.dragOverlayRow}>
        <div className={styles.dragHandleCell}>
          <div className={cx(styles.dragHandle)}>
            <DragHandleIcon />
          </div>
        </div>
        <RowContent
          shouldRegisterMentionRemovalRef={{ current: false }}
          scheduleItem={scheduleItem}
          scheduleType={scheduleType}
          scheduleItems={scheduleItems}
        />
      </div>
    </div>
  );
}

function DragHandleIcon() {
  return (
    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.5 7C9.32843 7 10 6.32843 10 5.5C10 4.67157 9.32843 4 8.5 4C7.67157 4 7 4.67157 7 5.5C7 6.32843 7.67157 7 8.5 7Z"
        fill="#757575"
      />
      <path
        d="M15.25 7C16.0784 7 16.75 6.32843 16.75 5.5C16.75 4.67157 16.0784 4 15.25 4C14.4216 4 13.75 4.67157 13.75 5.5C13.75 6.32843 14.4216 7 15.25 7Z"
        fill="#757575"
      />
      <path
        d="M8.5 13.375C9.32843 13.375 10 12.7034 10 11.875C10 11.0466 9.32843 10.375 8.5 10.375C7.67157 10.375 7 11.0466 7 11.875C7 12.7034 7.67157 13.375 8.5 13.375Z"
        fill="#757575"
      />
      <path
        d="M15.25 13.375C16.0784 13.375 16.75 12.7034 16.75 11.875C16.75 11.0466 16.0784 10.375 15.25 10.375C14.4216 10.375 13.75 11.0466 13.75 11.875C13.75 12.7034 14.4216 13.375 15.25 13.375Z"
        fill="#757575"
      />
      <path
        d="M8.5 19.75C9.32843 19.75 10 19.0784 10 18.25C10 17.4216 9.32843 16.75 8.5 16.75C7.67157 16.75 7 17.4216 7 18.25C7 19.0784 7.67157 19.75 8.5 19.75Z"
        fill="#757575"
      />
      <path
        d="M15.25 19.75C16.0784 19.75 16.75 19.0784 16.75 18.25C16.75 17.4216 16.0784 16.75 15.25 16.75C14.4216 16.75 13.75 17.4216 13.75 18.25C13.75 19.0784 14.4216 19.75 15.25 19.75Z"
        fill="#757575"
      />
    </svg>
  );
}
